/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
} from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  throwError,
  defer,
  of,
  timer,
  tap,
  catchError,
  switchMap,
  map,
  finalize,
  shareReplay,
} from 'rxjs';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import type {
  ApiResponse,
  AuthContent,
  DecodedToken,
  SafeUserData,
  UserData,
} from '@nebula-shared/domain';
import { HttpService } from './http/http.service';
import { TokenBridgeService } from './token-bridge.service';
import { VaultStorageService } from './vault-store-service';
import {
  extractVaultTenantIdFromJwtPayload,
  extractVaultTenantIdFromRecord,
  persistVaultTenantScopeToStorage,
  syncVaultTenantScopeFromAccessToken,
} from './vault-tenant-scope.util';
import { VAULT_CONFIG } from './vault-config.token';
import {
  clearVaultNavigationCache,
  clearVaultSessionCookies,
  clearVaultSessionStorage,
} from './vault-session-storage.util';
import { clearVaultTenantSnapshot } from './vault-tenant-snapshot.util';
import {
  clearVaultOperativeRoleScope,
  isVaultOperativeScopeHydrated,
  readVaultOperativeRole,
} from './vault-operative-role.util';
import {
  inferSuperadminFromUserRecord,
  resolveVaultQaRoleProfile,
  syncVaultSuperadminInStoredUserData,
  userCanAssignRepositoryFilesFromSession,
  userIsAdminEmpresaFromSession,
  userIsSuperadminFromSession,
} from './vault-menu/vault-role-menu-matrix.util';
import { collectSimappeMenuRoleIds, syncRoleIdsIntoUserData } from './vault-menu-role-ids.util';
import { VaultOperativeRoleSyncService } from './vault-operative-role-sync.service';
import { resolveTokenExpiryTimestamp } from './vault-token-expiry.util';
import { extractAuthContentFromRefreshResponse as parseRefreshAuthContent } from './vault-refresh-response.util';
import { VaultAuthLogoutService } from './vault-auth-logout.service';
import { isVaultLoginRoute, isVaultAccountBlockedCircuitOpen } from './vault-account-blocked.util';
import {
  captureVaultSessionForExpiryLogout,
  clearVaultExpiryRestoreSession,
  readVaultLogoutReason,
  setVaultPostLoginRouteToDashboard,
  vaultExpiryRestoreBelongsToUser,
} from './vault-post-login-redirect.util';
import { clearVaultTabsSnapshotSession } from './vault-nav-restore.util';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly tokenBridge = inject(TokenBridgeService);
  private readonly vaultStorage = inject(VaultStorageService);
  private readonly vaultConfig = inject(VAULT_CONFIG);
  private readonly operativeRoleSync = inject(VaultOperativeRoleSyncService);
  private readonly vaultAuthLogout = inject(VaultAuthLogoutService);

  private readonly AUTH_ENDPOINT: string = `/simappe-oauth2-server/api/v1`;

  readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  readonly isRefreshingSubject = new BehaviorSubject<boolean>(false);

  private accessTokenValue: string | null = null;
  private refreshTokenValue: string | null = null;

  private currentUser: UserData | null = null;
  private sessionId: string | null = null;
  private refreshTokenTimeout: any;

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public isRefreshing$ = this.isRefreshingSubject.asObservable();
  private isRefreshingLock = false;
  /** Un solo POST /refresh en vuelo; callers concurrentes reutilizan el mismo Observable. */
  private inFlightRefresh$: Observable<AuthContent> | null = null;
  private lastRefreshTimestamp = 0;
  private readonly REFRESH_DEBOUNCE_MS = 2000;
  private readonly MAX_REFRESH_RETRIES = 3;
  private refreshFailureCount = 0;
  private activityRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    if (typeof window === 'undefined') return;

    const token = this.getToken();
    const expiry = this.getTokenExpiry();
    const hasValidToken = token && expiry && Date.now() < expiry - 30000;

    if (hasValidToken) {
      this.loadUserFromToken();
      this.repairTokenExpiryFromJwt();
      this.isAuthenticatedSubject.next(true);
      this.scheduleTokenRefresh();
      this.syncVaultOperativeRoleAfterAuth({ resetScope: false });
    } else {
      this.isAuthenticatedSubject.next(false);
    }
  }

  /**
   * Realinea `token_expiry` contra el `exp` del propio access token.
   *
   * El `exp` del JWT es lo que el backend hace valer, así que manda sobre cualquier vencimiento
   * derivado de `expiresIn`. La corrección va en los dos sentidos: un valor guardado más largo que
   * el real es tan dañino como uno más corto, porque deja el refresco programado después de que el
   * token ya murió y la sesión solo se entera al recibir un 401.
   */
  private repairTokenExpiryFromJwt(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const token = this.getToken();
    if (!token) {
      return;
    }

    const decoded = this.decodeToken(token) as { exp?: number } | null;
    const expSeconds = decoded?.exp;
    if (!expSeconds || !Number.isFinite(expSeconds)) {
      return;
    }

    const fromJwt = expSeconds * 1000;
    const stored = this.getTokenExpiry();

    // La holgura absorbe el desfase entre el instante en que el servidor firma el token y aquel en
    // que el front guarda el vencimiento; más allá de eso, el JWT tiene la última palabra.
    if (!stored || Math.abs(stored - fromJwt) > 5_000) {
      localStorage.setItem('token_expiry', String(fromJwt));
    }
  }

  private loadUserFromToken(): void {
    const token = this.getToken();
    if (token) {
      const decoded = this.decodeToken(token);
      if (decoded?.User) {
        this.currentUser = decoded.User;
        this.sessionId = decoded.User.sessionId;
        this.accessTokenValue = token;
        this.refreshTokenValue = this.getRefreshToken();

        if (typeof window !== 'undefined') {
          localStorage.setItem(
            'user_data',
            JSON.stringify(this.extractSafeUserData(decoded.User)),
          );
          syncRoleIdsIntoUserData();
          this.persistVaultScopeFromCompany(
            decoded.User,
            decoded as unknown as Record<string, unknown>,
          );
        }
      }
    }
  }

  /**
   * Tras elegir empresa Nebula: persiste `tenant_id` del JWT (Datavault).
   * `companyId` tipo `7` / `7:1` es id Nebula; el tenant UUID viene en `tenant_id` del token.
   */
  private persistVaultScopeFromCompany(
    user: UserData | null | undefined,
    tokenPayload?: Record<string, unknown> | null,
  ): void {
    if (typeof window === 'undefined') {
      return;
    }
    const scope =
      extractVaultTenantIdFromJwtPayload(tokenPayload ?? null) ||
      extractVaultTenantIdFromRecord(user as unknown as Record<string, unknown>);
    if (!scope) {
      return;
    }
    persistVaultTenantScopeToStorage(scope);
  }

  /** Re-sincroniza tenant desde el JWT actual (p. ej. tras cambio de empresa). */
  public syncVaultTenantFromAccessToken(): void {
    syncVaultTenantScopeFromAccessToken();
  }

  public isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  handleSuccessfulAuth(
    content: AuthContent,
    options?: { resetProductSelection?: boolean },
  ): void {
    const resetProductSelection = options?.resetProductSelection !== false;
    const jwtUser = this.decodeToken(content.accessToken)?.User;
    const incomingUserEmail =
      (jwtUser?.email && String(jwtUser.email).trim()) ||
      (jwtUser?.username && String(jwtUser.username).trim()) ||
      null;
    const restoreBelongsToUser = vaultExpiryRestoreBelongsToUser(incomingUserEmail);
    const pendingExpiryRestore = readVaultLogoutReason() === 'expiry' && restoreBelongsToUser;
    if (resetProductSelection) {
      this.clearStaleSessionSelection();
      // Login fresco: no heredar pin de rol admin/superadmin de la sesión anterior.
      clearVaultOperativeRoleScope();
      if (!pendingExpiryRestore) {
        clearVaultNavigationCache();
        // Usuario distinto al que expiró: no arrastrar sus pestañas/ruta.
        clearVaultExpiryRestoreSession();
      }
    }

    this.accessTokenValue = content.accessToken;
    if (content.refreshToken) {
      this.refreshTokenValue = content.refreshToken;
    }
    if (content.sessionId) {
      this.sessionId = content.sessionId;
    }
    this.refreshFailureCount = 0;

    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', content.accessToken);
      if (content.refreshToken) {
        localStorage.setItem('refresh_token', content.refreshToken);
      }

      const expiryTime = resolveTokenExpiryTimestamp(content.expiresIn);
      if (expiryTime) {
        localStorage.setItem('token_expiry', expiryTime.toString());
      }
    }

    const decoded = this.decodeToken(content.accessToken);
    if (decoded?.User) {
      this.currentUser = decoded.User;
      this.saveSafeUserDataToCookie(this.currentUser);

      const safeData = this.extractSafeUserData(this.currentUser);
      localStorage.setItem('user_data', JSON.stringify(safeData));
      this.persistVaultScopeFromCompany(
        decoded.User,
        decoded as unknown as Record<string, unknown>,
      );
    }

    this.isAuthenticatedSubject.next(true);

    this.scheduleTokenRefresh();

    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('session_expired_shown');
      syncRoleIdsIntoUserData();
      this.tokenBridge.updateCookie();
      if (!isVaultLoginRoute()) {
        this.vaultStorage.resumeCookieSync();
        this.syncVaultOperativeRoleAfterAuth({ resetScope: false });
      }
    }
  }

  /** Hidrata rol Vault antes de calcular restricciones de módulo. */
  private syncVaultOperativeRoleAfterAuth(options?: { resetScope?: boolean }): void {
    if (typeof window === 'undefined') return;
    if (isVaultLoginRoute() || isVaultAccountBlockedCircuitOpen()) return;
    if (options?.resetScope !== false) {
      clearVaultOperativeRoleScope();
    }
    this.operativeRoleSync.clearSyncCache();
    this.operativeRoleSync.syncFromApi({ force: true }).subscribe(() => {
      syncVaultSuperadminInStoredUserData();
    });
  }

  private clearStaleSessionSelection(): void {
    if (typeof window === 'undefined') return;

    localStorage.removeItem('hasMultipleModules');
    localStorage.removeItem('selectedProductId');
    localStorage.removeItem('selectedRoute');
    localStorage.removeItem('selectedModule');
    sessionStorage.removeItem('hasMultipleModules');
    sessionStorage.removeItem('selectedProductId');
    sessionStorage.removeItem('selectedRoute');
    sessionStorage.removeItem('selectedModule');
  }

  public refreshToken(): Observable<AuthContent> {
    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    this.inFlightRefresh$ = defer(() => this.executeRefreshToken()).pipe(
      finalize(() => {
        this.inFlightRefresh$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlightRefresh$;
  }

  private executeRefreshToken(): Observable<AuthContent> {
    const memoryToken = this.refreshTokenValue;
    const storageToken =
      typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
    const refreshToken = memoryToken === storageToken ? memoryToken : storageToken;

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    if (typeof window !== 'undefined') {
      const refreshCount = Number.parseInt(sessionStorage.getItem('refresh_count') || '0', 10) + 1;
      sessionStorage.setItem('refresh_count', refreshCount.toString());
    }

    const applyRefreshResponse = (response: unknown): AuthContent => {
      const content = parseRefreshAuthContent(response);
      if (!content?.accessToken) {
        throw new HttpErrorResponse({
          status: 502,
          statusText: 'Invalid refresh response',
          url: `${this.AUTH_ENDPOINT}/refresh`,
          error: { message: 'Refresh 200 without accessToken' },
        });
      }
      return {
        ...content,
        refreshToken: content.refreshToken || refreshToken,
      };
    };

    return this.http.post<AuthContent>(`${this.AUTH_ENDPOINT}/refresh`, { refreshToken }).pipe(
      map((response) => applyRefreshResponse(response)),
      tap((content) => {
        this.handleSuccessfulAuth(content, { resetProductSelection: false });
      }),
      catchError((error: unknown) => {
        const httpError = error instanceof HttpErrorResponse ? error : null;
        const status = httpError?.status;

        if (status === 500 && this.refreshFailureCount < this.MAX_REFRESH_RETRIES) {
          this.refreshFailureCount++;
          const delay = Math.min(1000 * Math.pow(2, this.refreshFailureCount), 10000);

          return timer(delay).pipe(
            switchMap(() =>
              this.http.post<AuthContent>(`${this.AUTH_ENDPOINT}/refresh`, { refreshToken }),
            ),
            map((response) => applyRefreshResponse(response)),
            tap((content) => {
              this.handleSuccessfulAuth(content, { resetProductSelection: false });
              this.refreshFailureCount = 0;
            }),
            catchError((retryError: unknown) => {
              const retryHttp = retryError instanceof HttpErrorResponse ? retryError : null;
              if (retryHttp?.status === 401 || retryHttp?.status === 403) {
                this._forceLogout(true);
              }
              return throwError(() => retryError);
            }),
          );
        }

        // 502 = body inválido: no cerrar sesión; el access token actual puede seguir válido.
        if (status === 401 || status === 403) {
          this._forceLogout(true);
        }

        return throwError(() => error);
      }),
    );
  }

  private _forceLogout(showAlert = true): void {
    if (typeof window !== 'undefined' && readVaultLogoutReason() !== 'expiry') {
      captureVaultSessionForExpiryLogout();
    }

    this.clearAllData();

    if (typeof window !== 'undefined') {
      if (showAlert && !sessionStorage.getItem('session_expired_shown')) {
        sessionStorage.setItem('session_expired_shown', 'true');
      }
      window.dispatchEvent(new CustomEvent('vault-session-updated'));
      window.location.assign(this.resolveLoginRedirectUrl());
    }
  }

  logoutServerSide(): Observable<void> {
    const accessToken = this.getToken();
    if (!accessToken) {
      return of(void 0);
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      AccessToken: accessToken,
      'Content-Type': 'application/json',
    });

    const body = { accessToken };

    return this.http
      .post<ApiResponse<void>>(`${this.AUTH_ENDPOINT}/logout`, body, undefined, headers)
      .pipe(
        map(() => void 0),
        catchError(() => of(void 0)),
      );
  }

  logout(): void {
    if (typeof window !== 'undefined') {
      if (readVaultLogoutReason() !== 'expiry') {
        setVaultPostLoginRouteToDashboard();
        clearVaultTabsSnapshotSession();
      }
    }

    const loginUrl = this.resolveLoginRedirectUrl();
    const accessToken = this.getToken();

    const finish = (): void => {
      this.clearAllData();
      if (typeof window !== 'undefined') {
        window.location.assign(loginUrl);
      }
    };

    if (!accessToken) {
      finish();
      return;
    }

    this.vaultAuthLogout
      .recordLogout$(accessToken)
      .pipe(finalize(() => finish()))
      .subscribe();
  }

  clearAllData(): void {
    this.accessTokenValue = null;
    this.refreshTokenValue = null;
    this.currentUser = null;
    this.sessionId = null;
    this.refreshFailureCount = 0;
    this.operativeRoleSync.clearSyncCache();

    if (typeof window !== 'undefined') {
      clearVaultSessionStorage();
      // Limpia nav/tabs en localStorage; no borra snapshot de restore por expiración.
      clearVaultNavigationCache();
      clearVaultTenantSnapshot();
      clearVaultSessionCookies(this.cookieService);
      this.tokenBridge.clearAppDataCookie();
      this.vaultStorage.clearAll();
    }

    this.isAuthenticatedSubject.next(false);
    this.isRefreshingSubject.next(false);
    this.isRefreshingLock = false;
    this.inFlightRefresh$ = null;
    this.clearRefreshTimeout();
    if (this.activityRefreshTimeout) {
      clearTimeout(this.activityRefreshTimeout);
      this.activityRefreshTimeout = null;
    }
  }

  private resolveLoginRedirectUrl(): string {
    const loginUrl = this.vaultConfig.loginUrl;
    if (typeof window === 'undefined') return loginUrl;

    if (loginUrl.startsWith('/')) {
      return `${window.location.origin}${loginUrl}`;
    }

    return loginUrl;
  }

  private resolveVaultIsSuperadmin(userData: UserData): boolean {
    const vaultUser = userData as UserData & {
      is_superadmin?: boolean;
      isSuperadmin?: boolean;
      superadmin?: boolean;
    };
    return (
      Boolean(vaultUser.is_superadmin) ||
      Boolean(vaultUser.isSuperadmin) ||
      Boolean(vaultUser.superadmin) ||
      inferSuperadminFromUserRecord(vaultUser as unknown as Record<string, unknown>)
    );
  }

  private saveSafeUserDataToCookie(userData: UserData): void {
    const isSuperadmin = this.resolveVaultIsSuperadmin(userData);
    const safeUserData = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      preferredLanguage: userData.preferredLanguage,
      roles: (userData.roles ?? []).map((r: any) => ({
        id: r.id,
        name: r.name,
        authority: r.authority,
      })),
      companyId: userData.companyId,
      customerId: userData.customerId,
      is_superadmin: isSuperadmin,
    };

    const baseDomain = window.location.hostname;
    const expireAfter = new Date();
    expireAfter.setDate(expireAfter.getDate() + 2);

    this.cookieService.set('user_profile', JSON.stringify(safeUserData), {
      expires: expireAfter,
      path: '/',
      domain: baseDomain,
      secure: true,
      sameSite: 'Strict',
    });
  }

  private extractSafeUserData(userData: UserData): SafeUserData {
    const rec = userData as unknown as Record<string, unknown>;
    const tenantId =
      extractVaultTenantIdFromRecord(rec) ||
      (typeof rec['tenant_id'] === 'string' ? rec['tenant_id'].trim() : '') ||
      (typeof rec['tenantId'] === 'string' ? rec['tenantId'].trim() : '');
    return {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      preferredLanguage: userData.preferredLanguage,
      roles: (userData.roles ?? []).map((r: any) => ({
        id: r.id,
        name: r.name,
        authority: r.authority,
      })),
      companyId: userData.companyId,
      customerId: userData.customerId,
      ...(tenantId ? { tenant_id: tenantId } : {}),
      is_superadmin: this.resolveVaultIsSuperadmin(userData),
    } as SafeUserData;
  }

  public getSafeUserData(): SafeUserData | null {
    try {
      const cookieData = this.cookieService.get('user_profile');
      if (cookieData) {
        return JSON.parse(cookieData);
      }
    } catch {
      /* ignore */
    }
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const raw = localStorage.getItem('user_data');
      if (raw) {
        return JSON.parse(raw) as SafeUserData;
      }
    } catch {
      return null;
    }
    return null;
  }

  /** Asegura `currentUser` desde JWT (p. ej. tras refresh sin recargar la página). */
  public ensureCurrentUserFromToken(): void {
    if (!this.currentUser) {
      this.loadUserFromToken();
    }
  }

  public isVaultSuperadmin(): boolean {
    if (userIsSuperadminFromSession()) {
      return true;
    }
    this.ensureCurrentUserFromToken();
    const current = this.getCurrentUser();
    if (current && this.resolveVaultIsSuperadmin(current)) {
      return true;
    }
    const safe = this.getSafeUserData() as (SafeUserData & { is_superadmin?: boolean }) | null;
    if (safe?.is_superadmin) {
      return true;
    }
    if (safe && inferSuperadminFromUserRecord(safe as unknown as Record<string, unknown>)) {
      return true;
    }
    const token = this.getToken();
    if (token) {
      const decoded = this.decodeToken(token);
      const user = decoded?.User;
      if (user && this.resolveVaultIsSuperadmin(user)) {
        return true;
      }
    }
    return userIsSuperadminFromSession();
  }

  public isVaultAdminEmpresa(): boolean {
    if (this.isVaultSuperadmin()) {
      return false;
    }
    if (isVaultOperativeScopeHydrated() || readVaultOperativeRole()) {
      return userIsAdminEmpresaFromSession();
    }
    const targets = ['admin_empresa', 'administrador_empresa', 'adminempresa'];
    if (targets.some((role) => this.hasRoleNormalized(role))) {
      return true;
    }
    return userIsAdminEmpresaFromSession();
  }

  /** Paridad legacy `documents`: solo superadmin o administrador de empresa. */
  public canAssignRepositoryFiles(): boolean {
    return userCanAssignRepositoryFilesFromSession();
  }

  private hasRoleNormalized(roleKey: string): boolean {
    const want = roleKey
      .trim()
      .toLowerCase()
      .replace(/^role_/, '')
      .replace(/[\s-]+/g, '_');
    const roles = this.getUserRoles();
    return roles.some((r: { name?: string; authority?: string }) => {
      const n = String(r?.name ?? '')
        .trim()
        .toLowerCase()
        .replace(/^role_/, '')
        .replace(/[\s-]+/g, '_');
      const a = String(r?.authority ?? '')
        .trim()
        .toLowerCase()
        .replace(/^role_/, '')
        .replace(/[\s-]+/g, '_');
      return n === want || a === want;
    });
  }

  private persistSuperadminInUserData(): void {
    const user = this.getCurrentUser();
    if (!user || typeof window === 'undefined') {
      return;
    }
    try {
      const safe = this.extractSafeUserData(user);
      localStorage.setItem('user_data', JSON.stringify(safe));
      this.saveSafeUserDataToCookie(user);
    } catch {
      /* ignore */
    }
  }

  public handle401Error<T = any>(
    req: HttpRequest<T>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<T>> {
    // /refresh es público en errorInterceptor; si llega aquí, no reintentar ni logout duplicado.
    if (req.url.includes('/refresh')) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: 'Unauthorized',
            url: req.url,
          }),
      ) as unknown as Observable<HttpEvent<T>>;
    }

    const now = Date.now();
    const isDebounced =
      !this.inFlightRefresh$ && now - this.lastRefreshTimestamp < this.REFRESH_DEBOUNCE_MS;

    // Refresh reciente exitoso: reintentar con el access token actual.
    if (isDebounced) {
      const token = this.getToken();
      if (token) {
        const cloned = req.clone({
          headers: req.headers
            .set('Authorization', `Bearer ${token}`)
            .set('AccessToken', token),
        });
        return next(cloned) as unknown as Observable<HttpEvent<T>>;
      }
    }

    this.isRefreshingLock = true;
    this.isRefreshingSubject.next(true);

    const refreshResult$ = this.refreshToken().pipe(
      switchMap((content: AuthContent) => {
        this.isRefreshingLock = false;
        this.isRefreshingSubject.next(false);
        this.lastRefreshTimestamp = Date.now();
        this.scheduleTokenRefresh();

        const newReq = req.clone({
          headers: req.headers
            .set('Authorization', `Bearer ${content.accessToken}`)
            .set('AccessToken', content.accessToken),
        });
        return next(newReq);
      }),
      catchError((error: HttpErrorResponse) => {
        this.isRefreshingLock = false;
        this.isRefreshingSubject.next(false);
        // Logout ya lo hace executeRefreshToken() ante 401/403.
        return throwError(() => error);
      }),
    );

    return refreshResult$ as unknown as Observable<HttpEvent<T>>;
  }

  /** Actividad del usuario: adelantar refresh si el token vence pronto. */
  public noteUserActivity(): void {
    if (!this.isAuthenticated()) {
      return;
    }

    this.scheduleTokenRefresh();

    const expiryTime = this.getTokenExpiry();
    if (!expiryTime) {
      return;
    }

    const remaining = expiryTime - Date.now();
    if (remaining > 5 * 60 * 1000) {
      return;
    }

    if (this.activityRefreshTimeout) {
      clearTimeout(this.activityRefreshTimeout);
    }

    this.activityRefreshTimeout = setTimeout(() => {
      if (!this.getRefreshToken()) {
        return;
      }
      this.refreshToken().subscribe({
        error: () => {
          /* El interceptor / _forceLogout gestionan sesión inválida. */
        },
      });
    }, 2000);
  }

  private scheduleTokenRefresh(): void {
    this.clearRefreshTimeout();

    const expiryTime = this.getTokenExpiry();
    if (!expiryTime) return;

    const timeUntilExpiry = expiryTime - Date.now();

    const refreshTime = Math.max(timeUntilExpiry - 60_000, 1000);

    if (refreshTime > 0 && timeUntilExpiry > 0) {
      this.refreshTokenTimeout = setTimeout(() => {
        this.refreshToken().subscribe({
          error: () => {
            /* Evita rechazos no capturados en consola. */
          },
        });
      }, refreshTime);
    }
  }

  private clearRefreshTimeout(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
  }

  public getToken(): string | null {
    return (
      this.accessTokenValue ||
      (typeof window !== 'undefined' ? localStorage.getItem('access_token') : null)
    );
  }

  public getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;

    const storageToken = localStorage.getItem('refresh_token');

    this.refreshTokenValue = storageToken;

    return storageToken;
  }

  public getSessionId(): string | null {
    return this.sessionId;
  }

  public getCurrentUser(): UserData | null {
    return this.currentUser;
  }

  public getUserRoles() {
    return this.currentUser?.roles || this.getSafeUserData()?.roles || [];
  }

  /** IDs numéricos para menú Simappe (`read-by-roles-grouped`). */
  public getMenuRoleIds(): number[] {
    const fromMemory = this.getUserRoles()
      .map((r: { id?: unknown }) =>
        typeof r?.id === 'number' ? r.id : Number(r?.id),
      )
      .filter((id) => Number.isFinite(id) && id > 0);

    if (fromMemory.length > 0) {
      return [...new Set(fromMemory)];
    }

    return collectSimappeMenuRoleIds();
  }

  public getUserPermissions() {
    const roles = this.currentUser?.roles || this.getSafeUserData()?.roles || [];
    return roles.map((r: any) => r.authority);
  }

  public getCompanyId(): number | null {
    return this.currentUser?.companyId || this.getSafeUserData()?.companyId || null;
  }

  public getCustomerId(): number | null {
    return this.currentUser?.customerId || this.getSafeUserData()?.customerId || null;
  }

  public getEnvironment(): string | null {
    return this.currentUser?.environment || null;
  }

  public getPreferredLanguage(): string | null {
    return this.currentUser?.preferredLanguage || this.getSafeUserData()?.preferredLanguage || null;
  }

  public isCompanyLocked(): boolean {
    return this.currentUser?.companyLocked || false;
  }

  public isLegacyUser(): boolean {
    return this.currentUser?.legacySystemUser || false;
  }

  public getConnectionContext(): string | null {
    return this.currentUser?.connectionContext || null;
  }

  public getTokenExpiry(): number | null {
    if (typeof window === 'undefined') return null;
    const expiry = localStorage.getItem('token_expiry');
    return expiry ? parseInt(expiry, 10) : null;
  }

  public hasRole(roleName: string): boolean {
    const roles = this.currentUser?.roles || this.getSafeUserData()?.roles || [];
    return roles.some((r: any) => r.name === roleName || r.authority === roleName);
  }

  public hasAllRoles(roleNames: string[]): boolean {
    return roleNames.every((role) => this.hasRole(role));
  }

  private hasValidToken(): boolean {
    if (typeof window === 'undefined') return false;

    const token = this.getToken();
    const expiry = this.getTokenExpiry();

    if (!token || !expiry) return false;

    return Date.now() < expiry - 30000;
  }

  private decodeToken(token: string): DecodedToken | null {
    try {
      const part = token.split('.')[1];
      if (!part) {
        return null;
      }
      const padded = part.replace(/-/g, '+').replace(/_/g, '/');
      const pad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
      return JSON.parse(atob(pad)) as DecodedToken;
    } catch {
      return null;
    }
  }

  public isSessionActive(): boolean {
    const isValid = this.hasValidToken();

    if (!isValid) {
      this.clearAllData();
      return false;
    }

    return true;
  }

  public validateTokenAndGetSession(): Observable<UserData> {
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token disponible'));
    }

    const headers = new HttpHeaders({
      AccessToken: token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });

    const endpoint = `${this.AUTH_ENDPOINT}/jwt-get`;

    return this.http.post<UserData>(endpoint, {}, undefined, headers).pipe(
      map((response: ApiResponse<UserData>) => {
        return response.response.content;
      }),
      tap((userData: UserData) => {
        this.currentUser = userData;
        this.sessionId = userData.sessionId;
        this.saveSafeUserDataToCookie(userData);
        localStorage.setItem('user_data', JSON.stringify(this.extractSafeUserData(userData)));
        this.persistVaultScopeFromCompany(
          userData,
          userData as unknown as Record<string, unknown>,
        );

        if (typeof window !== 'undefined') {
          syncRoleIdsIntoUserData();
          this.tokenBridge.updateCookie();
          if (!isVaultLoginRoute()) {
            this.vaultStorage.resumeCookieSync();
            this.syncVaultOperativeRoleAfterAuth({ resetScope: false });
          }
        }
      }),
      catchError((error: unknown) => {
        return throwError(() => error);
      }),
    );
  }

  public validateTokenOnly(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      return of(false);
    }

    return this.http
      .post<boolean>(
        `${this.AUTH_ENDPOINT}/jwt`,
        {},
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          }),
        },
      )
      .pipe(
        map((response: ApiResponse<boolean>) => response.response.content),
        catchError(() => {
          return of(false);
        }),
      );
  }

  public updateTokensAfterCompanySelection(
    accessToken: string,
    refreshToken: string | undefined,
    expiresIn: number | undefined,
  ): void {
    this.accessTokenValue = accessToken;
    if (refreshToken) this.refreshTokenValue = refreshToken;

    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      const expiryTime = resolveTokenExpiryTimestamp(expiresIn);
      if (expiryTime) {
        localStorage.setItem('token_expiry', expiryTime.toString());
      }
    }

    const decoded = this.decodeToken(accessToken);
    if (decoded?.User) {
      const mergedUser = this.mergeSimappeRolesAfterCompanyToken(decoded.User);
      this.currentUser = mergedUser;
      this.sessionId = mergedUser.sessionId;
      this.saveSafeUserDataToCookie(mergedUser);
      localStorage.setItem('user_data', JSON.stringify(this.extractSafeUserData(mergedUser)));
      this.persistVaultScopeFromCompany(
        mergedUser,
        decoded as unknown as Record<string, unknown>,
      );
    }

    this.isAuthenticatedSubject.next(true);
    this.scheduleTokenRefresh();

    if (this.tokenBridge) {
      this.tokenBridge.updateCookie();
    }

    if (typeof window !== 'undefined') {
      syncRoleIdsIntoUserData();
      window.dispatchEvent(new CustomEvent('vault-session-updated'));
    }
  }

  /**
   * `jwt-select-company` a veces emite un JWT sin `User.roles` (p. ej. consulta/auditor).
   * Sin roles Simappe el guard de productos cancela la navegación y el login queda en blanco.
   */
  private mergeSimappeRolesAfterCompanyToken(user: UserData): UserData {
    const incoming = Array.isArray(user.roles) ? user.roles : [];
    if (incoming.length > 0) {
      return user;
    }

    const priorRoles =
      (Array.isArray(this.currentUser?.roles) && this.currentUser.roles.length > 0
        ? this.currentUser.roles
        : this.getSafeUserData()?.roles) ?? [];

    if (priorRoles.length === 0) {
      return user;
    }

    return { ...user, roles: priorRoles as UserData['roles'] };
  }

  // ========== NUEVOS MÉTODOS PARA VALIDACIÓN DE VAULT ==========

  /**
   * Verifica si el usuario actual pertenece a Vault (Datavault)
   */
  public isVaultUser(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Criterios para identificar usuario de Vault
    return (
      // Por NIT
      (user as any).nit?.startsWith('DATAVAULT') ||
      // Por email
      user.email?.includes('@datavault.com') ||
      // Por customerId específico de Datavault
      user.customerId === 8 ||
      (user as any).customer?.id === 8 ||
      // Por nombre del customer
      (user as any).customer?.customerName?.includes('Datavault') ||
      (user as any).name?.includes('Datavault')
    );
  }

  /**
   * Obtiene el tipo de tenant del usuario actual
   */
  public getUserTenantType(): 'vault' | 'erp' | 'unknown' {
    if (!this.isSessionActive()) return 'unknown';

    if (this.isVaultUser()) {
      return 'vault';
    }

    return 'erp';
  }

  /**
   * Tras validar acceso Vault en login; reanuda sync de cookies (pausado en handleSuccessfulAuth en /login).
   */
  public resumeVaultCookieSyncAfterLogin(): void {
    if (typeof window === 'undefined') return;
    this.vaultStorage.resumeCookieSync();
  }

  /**
   * Limpia completamente la sesión (útil para usuarios no autorizados)
   */
  public forceClearSession(): void {
    this.clearAllData();
  }

  /**
   * Valida si el usuario actual es de Vault
   * @returns true si es usuario Vault, false en caso contrario
   */
  public validateVaultAccess(): boolean {
    if (!this.isSessionActive()) {
      return false;
    }

    if (!this.isVaultUser()) {
      this.forceClearSession();
      return false;
    }

    return true;
  }
}
